import classNames from 'classnames';
import React, {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {StepCard} from '@udemy/react-certification-preparation';

import {CertificationVoucherProviderType} from 'src/generated/graphql';

import {trackClickExamLink} from '../../events';
import styles from './description.module.less';
import {
    QuestionAnswerItem,
    type QuestionAnswer as QuestionAnswerType,
} from './question-answer.react-component';

export interface DescriptionProps {
    examTitle: string;
    examId: string;
    examProviderType: CertificationVoucherProviderType;
    poolId: string;
    questionAnswers: QuestionAnswerType[];
    discountPercentage: string;
    hasDiscount?: boolean;
}

export const Description: React.FC<DescriptionProps> = ({
    questionAnswers,
    discountPercentage,
    hasDiscount = false,
    examTitle,
    examId,
    examProviderType,
    poolId,
}) => {
    const {gettext, interpolate} = useI18n();

    // TODO: CER-437: Delete this as part of the cleanup where the bullet points will stored in the backend.
    const isLinuxFoundationPage = useMemo(
        () => examProviderType === CertificationVoucherProviderType.LinuxFoundation,
        [examProviderType],
    );

    const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;

        const linkElement = target.closest('a');

        if (linkElement) {
            trackClickExamLink({
                name: examTitle,
                examId: examId,
                poolId: poolId,
                voucherId: null,
            });
        }
    };

    return (
        <div className={classNames(styles.container)}>
            <div className={classNames(styles.centered, 'ud-container')}>
                <div className={styles.steps}>
                    <StepCard
                        number={1}
                        title={gettext('Learn and practice')}
                        description={gettext(
                            'Get ready for your exam with expert-led Udemy courses and practice tests',
                        )}
                        iconColor="color-red-100"
                        position="bottom"
                    />
                    <StepCard
                        number={2}
                        title={gettext('Purchase exam voucher')}
                        description={
                            hasDiscount
                                ? interpolate(
                                      gettext(
                                          'Purchase your exam voucher through Udemy and save %(discountPercentage)s%',
                                      ),
                                      {discountPercentage},
                                      true,
                                  )
                                : gettext('Purchase your exam voucher through Udemy')
                        }
                        iconColor="color-red-100"
                        position="bottom"
                    />
                    <StepCard
                        number={3}
                        title={gettext('Schedule and take your exam')}
                        description={
                            !isLinuxFoundationPage
                                ? gettext('Use your voucher code when scheduling your exam')
                                : gettext(
                                      "Use your voucher code when scheduling your exam, see 'How do I use my voucher below'.",
                                  )
                        }
                        iconColor="color-red-100"
                        position="bottom"
                    />
                </div>
                {questionAnswers.map((questionAnswer, index) => (
                    <QuestionAnswerItem
                        key={index}
                        questionAnswer={questionAnswer}
                        index={index}
                        handleContainerClick={handleContainerClick}
                    />
                ))}
            </div>
        </div>
    );
};
